import {gql} from '@apollo/client';
import {Stack, Grid, Text, Title, Tooltip, Box, createStyles, LoadingOverlay} from '@mantine/core';
import {DocumentNode} from 'graphql';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {redirectToSignin} from 'BenchlingUtil';
import {ActionOrigin, IDENTITY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import notifications from 'notifications';
import {USER_SUMMARY_FIELDS_FRAGMENT} from 'parties/users/UserSummaryFragment';
import TenantRoleListWithPopover from 'tenants/admin/TenantRoleListWithPopover';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import MantineEditableTextField, {
  type MantineEditableTextFieldProps,
} from 'ui-library/inputs/MantineEditableTextField';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import * as DateUtil from 'util/DateUtil';
import * as modals from 'util/Modals';
import {callV3Internal, type V3InternalRequestBody} from 'v3-internal-client';

import {UserPageQuery} from './__generated/UserPageQuery';
import {UserAPIKeySection, WarehouseCredentialsTable, UserMFASection} from './UserCredentialsSections';
import type {RefetchUserPage} from './UserPage';

const MAX_VISIBLE_ITEMS = 2;

type UserPageQueryUser = ExtractByTypename<UserPageQuery['user'], 'UserNode'>;
type UserEditableField = 'name' | 'username' | 'email';

export interface UserBasicSettingsTabProps {
  user: UserPageQueryUser;
  refetch: RefetchUserPage;
  isCurrentUser: boolean;
}

interface UserBasicSettingsAttributes {
  [key: string]: {
    value: React.ReactNode;
    span?: number;
    visible?: boolean;
  };
}

export const USER_BASIC_SETTINGS_TAB_USER_FRAGMENT: DocumentNode = gql`
  fragment UserBasicSettingsTabUser on UserNode {
    ...UserSummaryFields
    mfaEnabled
    activeWarehouseLogins {
      id
      username
      label
      createdAt
    }
    organizations {
      id
      name
      handle
      urlIdentifier
    }
    teams {
      id
      name
      handle
    }
    apiKey {
      createdAt
    }
    permissions {
      canResetMfa
    }
  }
  ${USER_SUMMARY_FIELDS_FRAGMENT}
`;

type EditableUserFieldProps = Pick<
  MantineEditableTextFieldProps,
  'value' | 'maxLength' | 'onCommit' | 'dataTestAttrs'
> & {loading: boolean};

function EditableUserField({loading, ...fieldProps}: EditableUserFieldProps): JSX.Element {
  return (
    <Box pos="relative" w="100%">
      <LoadingOverlay visible={loading} loaderProps={{size: 'xs'}} />
      <MantineEditableTextField disabled={loading} {...fieldProps} />
    </Box>
  );
}

export function UserAttribute({label, value}: {label: string; value: React.ReactNode}): JSX.Element {
  return (
    <Stack spacing="sm" pt="xs" pl="xs" align="flex-start">
      <Text fw={700}>{label}</Text>
      {value}
    </Stack>
  );
}

function TruncatedList({names, max = MAX_VISIBLE_ITEMS}: {names: Array<string>; max?: number}): JSX.Element {
  const shown = names.slice(0, max);
  const extraCount = names.length - shown.length;
  const more = extraCount > 0 ? ` +${extraCount} more` : '';
  const label = shown.join(', ') + (extraCount > 0 ? ',' : '');

  if (names.length === 0) {
    return <Text>None</Text>;
  }

  return extraCount > 0 ? (
    <Tooltip label={names.join(', ')} multiline={true} withArrow={true}>
      <Text>
        {label}
        <Text component="span" c="dimmed">
          {more}
        </Text>
      </Text>
    </Tooltip>
  ) : (
    <Text>{label}</Text>
  );
}

export async function confirmEmailChange({
  isCurrentUser,
  currentEmail,
  newEmail,
}: {
  isCurrentUser: boolean;
  currentEmail: string;
  newEmail: string;
}): Promise<boolean> {
  const message = (
    <Text>
      Are you sure you want to change {isCurrentUser ? 'your' : "this user's"} email from{' '}
      <Text component="strong" fw={700}>
        {currentEmail}
      </Text>{' '}
      to{' '}
      <Text component="strong" fw={700}>
        {newEmail}
      </Text>
      ? {isCurrentUser ? 'You' : 'The user'} will be signed out of all sessions and will need to sign back in
      and re-verify {isCurrentUser ? 'your' : 'their'} new email address.
    </Text>
  );

  return (await Promise.resolve(
    modals.confirm('Change User Email', message, ButtonStyle.NEGATIVE, {
      acceptText: 'Change Email',
      rejectText: 'Cancel',
      enableRedesign: true,
      promiseCompatible: true,
    })
  )) as boolean;
}

export default function UserBasicSettingsTab({
  user,
  refetch,
  isCurrentUser,
}: UserBasicSettingsTabProps): JSX.Element {
  const {partiesEnableNewUserProfileTacPage} = useRolloutFlags();
  const [loadingFields, setLoadingFields] = useState<Set<UserEditableField>>(new Set());
  const {classes} = styles();

  useOnMount(() => {
    analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.OPENED_PARTY_BASIC_SETTINGS_TAB, {
      objectType: 'user',
      objectId: user.id,
    });
  });

  const handleUserFieldChange = async (field: UserEditableField, rawValue: string): Promise<void> => {
    const value = rawValue.trim();
    if (!(await shouldApplyFieldChange(field, value))) {
      return;
    }
    setLoadingFields((prev) => new Set([...prev, field]));
    const body: V3InternalRequestBody<'User.Update'> = {[field]: value};
    await callV3Internal('User.Update', {pathParams: {user_id: user.id}, body})
      .then(
        async () => {
          analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.UPDATED_USER_FIELD, {
            objectType: 'user',
            objectId: user.id,
            action_origin: ActionOrigin.IDENTITY_USER_PROFILE_PAGE,
            user_field: field,
          });
          if (isCurrentUser && (field === 'email' || field === 'username')) {
            redirectToSignin();
            return;
          }
          await refetch();
          notifications.success('Successfully updated user information.');
        },
        (res: Response) => {
          analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.FAILED_USER_FIELD_EDIT, {
            objectType: 'user',
            objectId: user.id,
            action_origin: ActionOrigin.IDENTITY_USER_PROFILE_PAGE,
            user_field: field,
          });
          throw res;
        }
      )
      .catch((res: Response) => {
        notifications.notifyError(res);
      })
      .finally(() => {
        setLoadingFields((prev) => {
          const newSet = new Set(prev);
          newSet.delete(field);
          return newSet;
        });
      });
  };

  const shouldApplyFieldChange = async (field: UserEditableField, value: string): Promise<boolean> => {
    // The user node exposes `handle`, but the API (and this handler) call it `username`.
    const currentValue = field === 'username' ? user.handle : user[field];
    if (value === '' || currentValue === value) {
      return false;
    }
    if (field === 'email') {
      return await confirmEmailChange({isCurrentUser, currentEmail: user.email, newEmail: value});
    }
    if (field === 'username') {
      return await confirmUsernameChange();
    }
    return true;
  };

  const confirmUsernameChange = async (): Promise<boolean> => {
    const message =
      `Are you sure you want to change ${isCurrentUser ? 'your' : "this user's"} username?` +
      ` ${isCurrentUser ? 'You' : 'The user'} will be signed out of all sessions and need to sign back in.`;

    return await runConfirmDeleteModal({
      typeName: 'User Username',
      title: 'Change Username',
      message,
      acceptText: 'Change Username',
    });
  };

  const accountAttributes: UserBasicSettingsAttributes = {
    Name: {
      value: partiesEnableNewUserProfileTacPage ? (
        <EditableUserField
          value={user.name}
          maxLength={255}
          loading={loadingFields.has('name')}
          onCommit={(newValue) => handleUserFieldChange('name', newValue)}
        />
      ) : (
        <Text>{user.name}</Text>
      ),
    },
    Username: {
      value: partiesEnableNewUserProfileTacPage ? (
        <EditableUserField
          value={user.handle}
          maxLength={30}
          loading={loadingFields.has('username')}
          onCommit={(newValue) => handleUserFieldChange('username', newValue)}
          dataTestAttrs={{component: 'UserBasicSettingsTab', element: 'usernameField'}}
        />
      ) : (
        <Text>{user.handle}</Text>
      ),
    },
    Email: {
      value: partiesEnableNewUserProfileTacPage ? (
        <EditableUserField
          value={user.email}
          maxLength={255}
          loading={loadingFields.has('email')}
          onCommit={(newValue) => handleUserFieldChange('email', newValue)}
          dataTestAttrs={{component: 'UserBasicSettingsTab', element: 'emailField'}}
        />
      ) : (
        <Text>{user.email}</Text>
      ),
    },
    'Joined On': {
      value: <Text>{DateUtil.readableTime(user.joinedOn)}</Text>,
      visible: partiesEnableNewUserProfileTacPage,
    },
    Organizations: {
      value: <TruncatedList names={user.organizations.map((org) => org.name)} />,
      visible: partiesEnableNewUserProfileTacPage,
    },
    Teams: {
      value: <TruncatedList names={user.teams.map((team) => team.name)} />,
      visible: partiesEnableNewUserProfileTacPage,
    },
    Roles: {
      value: user.tenantCollaborations.length ? (
        <TenantRoleListWithPopover party={user} />
      ) : (
        <Text>None</Text>
      ),
      visible: partiesEnableNewUserProfileTacPage,
    },
  };

  const credentialsAttributes: UserBasicSettingsAttributes = {
    Password: {
      value: (
        <Text>
          {user.passwordLastChangedAt
            ? `Set on ${DateUtil.readableTime(user.passwordLastChangedAt)}`
            : 'Never changed'}
        </Text>
      ),
      visible: partiesEnableNewUserProfileTacPage,
    },
    'MFA Status': {
      value: <UserMFASection user={user} refetch={refetch} />,
      visible: BenchlingConfig.AUTH_ENABLE_MFA_FEATURE,
    },
    'API Keys': {
      value: <UserAPIKeySection user={user} refetch={refetch} />,
      span: 2,
      visible: partiesEnableNewUserProfileTacPage && !(user.apiKey == null),
    },
    'Warehouse Credentials': {
      value: <WarehouseCredentialsTable user={user} refetch={refetch} />,
      span: 2,
      visible: partiesEnableNewUserProfileTacPage && user.activeWarehouseLogins.length > 0,
    },
  };

  const sections: Array<{title: string; attributes: UserBasicSettingsAttributes; visible?: boolean}> = [
    {title: 'User Account', attributes: accountAttributes},
    {
      title: 'Credentials',
      attributes: credentialsAttributes,
      // This should get removed when both the LD flag and MFA feature goes GA
      visible: partiesEnableNewUserProfileTacPage || BenchlingConfig.AUTH_ENABLE_MFA_FEATURE,
    },
  ];

  return (
    <Box className={classes.basicSettingsTab} pt="xl">
      <Stack spacing="xl">
        {sections.map(
          ({title, attributes, visible = true}) =>
            visible && (
              <Stack key={title} spacing="xs">
                <Title order={4}>{title}</Title>
                <Grid columns={2}>
                  {Object.entries(attributes).map(
                    ([label, {value, span = 1, visible = true}]) =>
                      visible && (
                        <Grid.Col key={label} span={span} className={classes.basicSettingsText}>
                          <UserAttribute label={label} value={value} />
                        </Grid.Col>
                      )
                  )}
                </Grid>
              </Stack>
            )
        )}
      </Stack>
    </Box>
  );
}

const styles = createStyles((theme) => ({
  basicSettingsTab: {
    paddingInline: 'calc(100% / 22)',
    maxWidth: theme.breakpoints.lg,
  },
  basicSettingsText: {
    '& input, & .mantine-Text-root': {
      fontSize: theme.fontSizes.md,
      paddingLeft: 0,
    },
    '& input': {
      paddingLeft: theme.spacing.xs,
    },
  },
}));
